import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { DkimDomain, LicenseCode } from '@account-users/account/models';
import { LicenseV2 } from '@account-users/account/state/feature-toggles/models';

class Domain {
  static validDomainFormat(control: AbstractControl): ValidationErrors | null {
    const validDomainRegex = /^[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/u;
    return !validDomainRegex.test(control.value)
      ? { invalidDomainFormat: true }
      : null;
  }
}

@Component({
  selector: 'app-email-delivery-settings',
  templateUrl: './email-delivery-settings.component.html',
  styleUrls: ['./email-delivery-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailDeliverySettingsComponent implements OnChanges {
  @HostBinding('class.disabled') @Input() disabled: boolean;
  @Input() public dkimDomains: DkimDomain;
  @Input() public allLicenses: LicenseV2[];

  @Output() public update = new EventEmitter<DkimDomain>();

  public formGroup: FormGroup;
  public loading = false;

  public get domains() {
    return this.formGroup.get('domains') as FormArray;
  }

  public get hasFromDomainValidation() {
    return this.allLicenses?.find(
      (l) => l.code === LicenseCode.Rfc5322FromDomainValidation,
    )?.enabled;
  }

  constructor(private readonly fb: FormBuilder) {
    this.formGroup = this.fb.group({
      domains: this.fb.array([]),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const dkimDomains = changes['dkimDomains'];
    if (dkimDomains?.currentValue) {
      this.updateDomains(dkimDomains.currentValue);
    }
  }

  public addDomain(name?: string, dkimEnabled?: boolean) {
    const domain = this.fb.group({
      name: [name ?? '', [Validators.required, Domain.validDomainFormat]],
      dkimEnabled: [dkimEnabled ?? false],
    });
    this.domains.push(domain);
  }

  public enableDkimForDomain({ dkimEnabled, idx }) {
    const domain = this.domains.at(idx);
    domain.patchValue({ dkimEnabled });
  }

  public removeDomain(index: number) {
    this.domains.removeAt(index);
  }

  public saveChanges() {
    const { domains } = this.formGroup.value;
    const fromDomains = domains.reduce((a, b) => {
      const { name, dkimEnabled } = b;
      a[name] = { dkimEnabled };
      return a;
    }, {});

    const dkimBody: DkimDomain = {
      fromDomains,
      customerConfirmed: this.dkimDomains.customerConfirmed,
    };
    this.update.emit(dkimBody);
  }

  public disableSave() {
    const { fromDomains } = this.dkimDomains ?? { fromDomains: {} };

    if (this.domains.value.length !== Object.keys(fromDomains).length) {
      return false;
    }

    for (const domainValue of this.domains.value) {
      const { name, dkimEnabled } = domainValue;
      if (!fromDomains[name] || fromDomains[name].dkimEnabled !== dkimEnabled) {
        return false;
      }
    }
    return true;
  }

  private updateDomains(dkimDomains: DkimDomain) {
    this.domains.clear();
    const { fromDomains } = dkimDomains ?? {};
    Object.keys(fromDomains).forEach((domain) => {
      const { dkimEnabled } = fromDomains[domain];
      this.addDomain(domain, dkimEnabled);
    });
  }
}
